import { Alert, Button, Tag } from 'antd';
import { formatMoney } from '../../../../../shared/policies/format-money';
import { RATE_BASIS_LABELS } from '../../../../../shared/policies/rate-basis-labels';
import type { ManualPriceRequestView } from '../manual-pricing.api';

interface ManualPriceBannerProps {
  request: ManualPriceRequestView | null;
  canSubmit: boolean;
  canDecide: boolean;
  onEnterPrice: () => void;
  onDecide: (decision: 'approve' | 'reject') => void;
}

function statusLine(request: ManualPriceRequestView | null): string {
  if (!request) return 'The rate card has no price for this job. Enter one to send for approval.';
  const price = `${formatMoney(request.amount)} ${RATE_BASIS_LABELS[request.basis].toLowerCase()}`;
  if (request.status === 'pending_approval') return `${price} is waiting for approval. Reason given: ${request.reason}`;
  if (request.status === 'rejected') return `${price} was rejected: ${request.decisionReason}. Enter another price.`;
  return `${price} was approved.`;
}

/** The "Pricing required" alert of a job the rate card could not price: what is known, and the buttons that fit who is looking. */
export function ManualPriceBanner({ request, canSubmit, canDecide, onEnterPrice, onDecide }: ManualPriceBannerProps) {
  return (
    <Alert
      className="mb-5"
      type="warning"
      showIcon
      title={
        <div className="flex flex-wrap items-center gap-3">
          <Tag color="warning">Pricing required</Tag>
          <span>{statusLine(request)}</span>
          <div className="ml-auto flex gap-2">
            {canSubmit && (
              <Button size="small" type="primary" onClick={onEnterPrice}>
                Enter price
              </Button>
            )}
            {canDecide && (
              <>
                <Button size="small" onClick={() => onDecide('reject')}>
                  Reject
                </Button>
                <Button size="small" type="primary" onClick={() => onDecide('approve')}>
                  Approve
                </Button>
              </>
            )}
          </div>
        </div>
      }
    />
  );
}
